import { Geo, Logger } from "@identity/utils";
import {
  CommunicationService,
  eventsRepository,
} from "src/services/CommunicationService";
import {
  ConsentsEventParams,
  DataWarehouseEventType,
  DataWarehouseService,
  ModalAction,
  ModalCategory,
  ModalLabel,
} from "src/services/DataWarehouseService";

interface QueuedTrackingEvent {
  label: ModalLabel;
  action: ModalAction;
}

export class CMPTracker {
  protected UI_SHOWN;
  protected GA_CATEGORY;
  protected eventQueue: QueuedTrackingEvent[] = [];
  protected sendImmediately = false;
  protected gpcRegistered = false;
  protected cmpName;

  constructor(
    protected logger: Logger,
    protected dataWarehouse: DataWarehouseService,
    protected geo: Geo,
    protected communicationService: CommunicationService,
  ) {
    this.communicationService.once(eventsRepository.IDENTITY_READY, () => {
      this.sendImmediately = true;
      this.sendQueuedEvents();
    });
  }

  init() {
    this.logger.info(`Starting ${this.cmpName} tracker`);
    this.UI_SHOWN = `${this.cmpName}-ui-shown`;
    if (this.geo.isGdprCountry()) {
      this.GA_CATEGORY = `gdpr-modal-${this.cmpName}` as ModalCategory;
    } else if (this.geo.getCountryCode() === "us") {
      this.GA_CATEGORY = `ccpa-modal-${this.cmpName}` as ModalCategory;
    } else {
      this.logger.info(
        `Not a GDPR or CCPA country, skipping ${this.cmpName} tracker`,
        this.geo.getCountryCode(),
      );
    }
  }

  sendActionEvent(label) {
    this.logger.info("Queued sending action event");
    this.sendEvent(label);
  }

  sendImpressionEvent(label: ModalLabel = "modal-view") {
    this.logger.info("Queued sending impression event");
    this.sendEvent(label, "impression");
    this.trackLoadTime();
  }

  protected trackLoadTime() {
    this.dataWarehouse.track(DataWarehouseEventType.loadTime, this.UI_SHOWN);

    this.logger.info("Loading time tracking event sent");
  }

  protected sendEvent(
    label: ModalLabel,
    action: "click" | "impression" = "click",
  ) {
    if (
      !this.eventQueue.find((queuedEvent) => {
        return queuedEvent.label === label && queuedEvent.action === action;
      })
    ) {
      this.eventQueue.push({ label, action });
    }

    if (this.sendImmediately) {
      this.sendQueuedEvents();
    }
  }

  protected sendQueuedEvents() {
    while (this.eventQueue.length > 0) {
      const event = this.eventQueue.shift()!;
      const payload: ConsentsEventParams = {
        ga_category: this.GA_CATEGORY,
        ga_action: event.action,
        ga_label: event.label,
      };

      this.dataWarehouse.track(DataWarehouseEventType.consents, payload);

      this.logger.info("Action tracking event sent", event.label);
    }
  }
}
